import React, { useState, useEffect, useCallback, useMemo } from "react";
import { Check, Thermometer, Car, Coffee, ShoppingBag } from "lucide-react";
import ModuleNavigation from "../components/ModuleNavigation";
import { Pie } from "react-chartjs-2";
import {
  Chart,
  ArcElement,
  Tooltip,
  Legend,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
} from "chart.js";

// Register Chart.js components
Chart.register(
  ArcElement,
  Tooltip,
  Legend,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement
);

const UAECarbonDay = () => {
  const [activeSection, setActiveSection] = useState(1);
  const [completedSections, setCompletedSections] = useState({});
  const [userChoices, setUserChoices] = useState({
    ac: null,
    breakfast: null,
    commute: null,
    water: null,
    lunch: null,
    dinner: null,
    shopping: null,
  });
  const [dailyFootprint, setDailyFootprint] = useState(0);
  const [footprintBreakdown, setFootprintBreakdown] = useState({});
  const [pledgeMade, setPledgeMade] = useState("");
  const [resultsSaved, setResultsSaved] = useState(false);

  // New state variables for interactive zone
  const [rankingItems, setRankingItems] = useState([
    { id: 1, name: "🥩 Eating a beef burger", value: 3.0, userRank: null },
    { id: 2, name: "🚗 20km solo car commute", value: 3.8, userRank: null },
    { id: 3, name: "❄️ AC at 18°C for 8 hours", value: 5.2, userRank: null },
    {
      id: 4,
      name: "🧴 Drinking bottled water for a week",
      value: 0.6,
      userRank: null,
    },
    { id: 5, name: "👕 Buying a new t-shirt", value: 7.0, userRank: null },
    { id: 6, name: "🎬 4K streaming for 1 hour", value: 0.4, userRank: null },
    { id: 7, name: "✈️ Dubai-Abu Dhabi flight", value: 90, userRank: null },
  ]);
  const [showRankingResults, setShowRankingResults] = useState(false);
  const [draggedItemId, setDraggedItemId] = useState(null);

  // Simulator state
  const [simulatorChoices, setSimulatorChoices] = useState({
    breakfast: null,
    commute: null,
    cooling: null,
  });
  const [simulatorTotal, setSimulatorTotal] = useState(0);

  // Pledge section state
  const [selectedPledge, setSelectedPledge] = useState(null);
  const [pledgeSavings, setPledgeSavings] = useState({ weekly: 0, yearly: 0 });
  const [userEmail, setUserEmail] = useState("");
  const [showEmailForm, setShowEmailForm] = useState(false);
  const [pledgeDone, setPledgeDone] = useState(false);

  // Weekly tracker state
  const [trackerWeeks, setTrackerWeeks] = useState([
    {
      id: 1,
      name: "Week 1",
      days: [false, false, false, false, false, false, false],
    },
    {
      id: 2,
      name: "Week 2",
      days: [false, false, false, false, false, false, false],
    },
    {
      id: 3,
      name: "Week 3",
      days: [false, false, false, false, false, false, false],
    },
    {
      id: 4,
      name: "Week 4",
      days: [false, false, false, false, false, false, false],
    },
  ]);

  // Wrap emissionFactors with useMemo
  const emissionFactors = useMemo(
    () => ({
      ac: {
        "16-20°C": 3.6, // As specified
        "21-23°C": 2.5, // As specified
        "24-26°C": 1.7, // As specified
        "no-ac": 0, // As specified
      },
      breakfast: {
        "beef-lamb": 3.5, // As specified
        "chicken-eggs": 1.5, // As specified
        vegetarian: 0.3, // As specified
      },
      commute: {
        "drive-alone": 3.8, // As specified
        carpool: 1.3, // As specified
        "public-transport": 0.7, // As specified
        "walk-bike": 0, // As specified
      },
      water: {
        bottled: 0.2, // As specified
        occasionally: 0.05, // As specified
        filter: 0.01, // As specified
      },
      lunch: {
        "red-meat": 5.0, // As specified
        "chicken-fish": 2.0, // As specified
        vegetarian: 0.7, // As specified
      },
      dinner: {
        "red-meat": 5.0, // Same as lunch as specified
        "chicken-fish": 2.0, // Same as lunch as specified
        vegetarian: 0.7, // Same as lunch as specified
      },
      shopping: {
        weekly: 2.0, // Keep this value
        monthly: 0.5, // Keep this value
        rarely: 0.1, // Keep this value
      },
    }),
    []
  );

  // New simulator emission factors also need to be wrapped
  const simulatorEmissions = useMemo(
    () => ({
      breakfast: {
        beef: 3.5,
        eggs: 1.5,
        fruit: 0.3,
      },
      commute: {
        solo: 3.8,
        carpool: 1.3,
        metro: 0.7,
      },
      cooling: {
        "19c": 3.0,
        "22c": 2.2,
        "25c": 1.2,
      },
    }),
    []
  );

  const handleSectionComplete = (sectionNumber) => {
    setCompletedSections({ ...completedSections, [sectionNumber]: true });
  };

  const handleModuleComplete = () => {
    // Mark all sections as complete
    const allComplete = {};
    for (let i = 1; i <= 5; i++) {
      allComplete[i] = true;
    }
    setCompletedSections(allComplete);
    setResultsSaved(true);
  };

  // Wrap calculateFootprint in useCallback
  const calculateFootprint = useCallback(() => {
    let total = 0;
    let breakdown = {};

    Object.keys(userChoices).forEach((category) => {
      if (
        userChoices[category] &&
        emissionFactors[category][userChoices[category]]
      ) {
        const value = emissionFactors[category][userChoices[category]];
        total += value;
        breakdown[category] = value;
      }
    });

    setDailyFootprint(total);
    setFootprintBreakdown(breakdown);
    return total;
  }, [userChoices, emissionFactors]);

  // Ranking game functions
  const handleDragStart = (e, id) => {
    setDraggedItemId(id);
    if (e.dataTransfer) {
      e.dataTransfer.effectAllowed = "move";
    }
  };

  const handleDragOver = (e, id) => {
    e.preventDefault();
    if (draggedItemId === id) return;

    // Reorder the items
    const items = [...rankingItems];
    const draggedItemIndex = items.findIndex(
      (item) => item.id === draggedItemId
    );
    const dropTargetIndex = items.findIndex((item) => item.id === id);

    if (draggedItemIndex === -1 || dropTargetIndex === -1) return;

    const [reorderedItem] = items.splice(draggedItemIndex, 1);
    items.splice(dropTargetIndex, 0, reorderedItem);

    // Update the ranking
    const updatedItems = items.map((item, index) => ({
      ...item,
      userRank: index + 1,
    }));

    setRankingItems(updatedItems);
  };

  const handleDragEnd = () => {
    setDraggedItemId(null);
  };

  const checkRanking = () => {
    // Sort by actual values (highest to lowest)
    const correctOrder = [...rankingItems].sort((a, b) => b.value - a.value);
    const updatedItems = rankingItems.map((item) => {
      const correctPosition =
        correctOrder.findIndex((correctItem) => correctItem.id === item.id) + 1;
      return {
        ...item,
        correctRank: correctPosition,
        isCorrect: item.userRank === correctPosition,
      };
    });

    setRankingItems(updatedItems);
    setShowRankingResults(true);
  };

  const resetRanking = () => {
    const resetItems = rankingItems.map((item) => ({
      ...item,
      userRank: null,
      correctRank: null,
      isCorrect: null,
    }));
    setRankingItems(resetItems);
    setShowRankingResults(false);
  };

  // Simulator functions
  const handleSimulatorChoice = (category, choice, value) => {
    setSimulatorChoices({
      ...simulatorChoices,
      [category]: choice,
    });

    // Recalculate total
    const newChoices = { ...simulatorChoices, [category]: choice };
    let total = 0;

    if (newChoices.breakfast)
      total += simulatorEmissions.breakfast[newChoices.breakfast];
    if (newChoices.commute)
      total += simulatorEmissions.commute[newChoices.commute];
    if (newChoices.cooling)
      total += simulatorEmissions.cooling[newChoices.cooling];

    setSimulatorTotal(total);
  };

  const resetSimulator = () => {
    setSimulatorChoices({
      breakfast: null,
      commute: null,
      cooling: null,
    });
    setSimulatorTotal(0);
  };

  useEffect(() => {
    if (Object.values(userChoices).some((choice) => choice !== null)) {
      calculateFootprint();
    }
  }, [userChoices, calculateFootprint]);

  // Pledge functions
  const handlePledgeSelect = (type, savings) => {
    setSelectedPledge(type);
    setPledgeSavings({
      weekly: (savings / 52).toFixed(1),
      yearly: savings,
    });
  };

  const savePledge = () => {
    if (!pledgeMade) return;

    setPledgeDone(true);
    setShowEmailForm(true);
  };

  const saveEmail = () => {
    // In a real app, you would send this to a backend
    console.log("Saving email for reminders:", userEmail);
    console.log("Pledge:", pledgeMade);
    console.log("Selected pledge type:", selectedPledge);

    // Close email form and show success
    setShowEmailForm(false);
  };

  // Tracker functions
  const toggleTrackerDay = (weekId, dayIndex) => {
    setTrackerWeeks(
      trackerWeeks.map((week) =>
        week.id === weekId
          ? {
              ...week,
              days: week.days.map((day, idx) =>
                idx === dayIndex ? !day : day
              ),
            }
          : week
      )
    );
  };

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
      <div className="text-center mb-10">
        <h1 className="text-4xl font-bold text-primary-600 mb-2">
          Make It Visible: Your UAE Day in CO₂
          <span className="text-edu-orange">.</span>
        </h1>
        <div className="w-24 h-1 bg-edu-orange mx-auto mt-2 mb-6"></div>
      </div>

      {/* Module Banner */}
      <div className="bg-primary-600 p-8 rounded-lg">
        <h2 className="text-2xl font-bold mb-4">
          Your Daily Carbon Impact in the UAE
        </h2>
        <p className="text-lg mb-6">
          Most people in the Emirates add about 14 kg CO₂-eq to the atmosphere
          every day. Three everyday habits—cooling, driving, and what we
          eat/drink—make up the lion's share. In the next 20 minutes you'll see
          where that carbon hides in a typical UAE day and practise shrinking
          it.
        </p>
      </div>

      {/* Tab Navigation */}
      <div className="flex flex-wrap border-b border-gray-200 mb-8 overflow-x-auto relative">
        <button
          className={`px-4 py-2 mr-2 whitespace-nowrap ${
            activeSection === 1
              ? "text-primary-500 border-b-2 border-primary-500 font-medium"
              : "text-gray-600 hover:text-primary-500"
          }`}
          onClick={() => setActiveSection(1)}
        >
          1. Start with You
        </button>
        <button
          className={`px-4 py-2 mr-2 whitespace-nowrap ${
            activeSection === 2
              ? "text-primary-500 border-b-2 border-primary-500 font-medium"
              : "text-gray-600 hover:text-primary-500"
          }`}
          onClick={() => setActiveSection(2)}
        >
          2. Your Daily Emissions
        </button>
        <button
          className={`px-4 py-2 mr-2 whitespace-nowrap ${
            activeSection === 3
              ? "text-primary-500 border-b-2 border-primary-500 font-medium"
              : "text-gray-600 hover:text-primary-500"
          }`}
          onClick={() => setActiveSection(3)}
        >
          3. UAE Emissions Deep-Dive
        </button>
        <button
          className={`px-4 py-2 mr-2 whitespace-nowrap ${
            activeSection === 4
              ? "text-primary-500 border-b-2 border-primary-500 font-medium"
              : "text-gray-600 hover:text-primary-500"
          }`}
          onClick={() => setActiveSection(4)}
        >
          4. Interactive Zone
        </button>
        <button
          className={`px-4 py-2 mr-2 whitespace-nowrap ${
            activeSection === 5
              ? "text-primary-500 border-b-2 border-primary-500 font-medium"
              : "text-gray-600 hover:text-primary-500"
          }`}
          onClick={() => setActiveSection(5)}
        >
          5. Your Pledge
        </button>
      </div>

      {/* Tab Content */}
      <div className="mb-16">
        {activeSection === 1 && (
          <div className="bg-white shadow-md rounded-lg p-6">
            <h2 className="text-2xl font-semibold text-gray-800 mb-4">
              Start with You — Quick Daily Choices
            </h2>

            <p className="text-gray-700 mb-6">
              Let's walk through a typical day in your UAE life. No right or
              wrong — just real.
            </p>

            <div className="space-y-8">
              {/* AC Temperature Question */}
              <div className="p-4 bg-primary-50 rounded-lg">
                <h3 className="text-lg font-medium text-gray-800 mb-3 flex items-center">
                  <Thermometer className="mr-2 h-5 w-5 text-primary-600" />
                  What temperature do you usually keep your AC at?
                </h3>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-3">
                  <button
                    className={`p-4 rounded-md flex flex-col items-center ${
                      userChoices.ac === "16-20°C"
                        ? "bg-primary-100 border-2 border-primary-500"
                        : "bg-white border border-gray-200 hover:bg-gray-50"
                    }`}
                    onClick={() =>
                      setUserChoices({ ...userChoices, ac: "16-20°C" })
                    }
                  >
                    <span className="text-2xl mb-2">❄️❄️</span>
                    <span className="font-medium">16–20°C</span>
                    <span className="text-sm text-gray-500 mt-1">
                      Very Cool
                    </span>
                  </button>

                  <button
                    className={`p-4 rounded-md flex flex-col items-center ${
                      userChoices.ac === "21-23°C"
                        ? "bg-primary-100 border-2 border-primary-500"
                        : "bg-white border border-gray-200 hover:bg-gray-50"
                    }`}
                    onClick={() =>
                      setUserChoices({ ...userChoices, ac: "21-23°C" })
                    }
                  >
                    <span className="text-2xl mb-2">❄️</span>
                    <span className="font-medium">21–23°C</span>
                    <span className="text-sm text-gray-500 mt-1">Cool</span>
                  </button>

                  <button
                    className={`p-4 rounded-md flex flex-col items-center ${
                      userChoices.ac === "24-26°C"
                        ? "bg-primary-100 border-2 border-primary-500"
                        : "bg-white border border-gray-200 hover:bg-gray-50"
                    }`}
                    onClick={() =>
                      setUserChoices({ ...userChoices, ac: "24-26°C" })
                    }
                  >
                    <span className="text-2xl mb-2">🌡️</span>
                    <span className="font-medium">24–26°C</span>
                    <span className="text-sm text-gray-500 mt-1">Moderate</span>
                  </button>

                  <button
                    className={`p-4 rounded-md flex flex-col items-center ${
                      userChoices.ac === "no-ac"
                        ? "bg-primary-100 border-2 border-primary-500"
                        : "bg-white border border-gray-200 hover:bg-gray-50"
                    }`}
                    onClick={() =>
                      setUserChoices({ ...userChoices, ac: "no-ac" })
                    }
                  >
                    <span className="text-2xl mb-2">🌿</span>
                    <span className="font-medium">I don't use AC</span>
                    <span className="text-sm text-gray-500 mt-1">
                      Natural cooling
                    </span>
                  </button>
                </div>
              </div>

              {/* Breakfast Question */}
              <div className="p-4 bg-primary-50 rounded-lg">
                <h3 className="text-lg font-medium text-gray-800 mb-3 flex items-center">
                  <Coffee className="mr-2 h-5 w-5 text-primary-600" />
                  What do you usually eat for breakfast?
                </h3>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                  <button
                    className={`p-4 rounded-md flex flex-col items-center ${
                      userChoices.breakfast === "beef-lamb"
                        ? "bg-primary-100 border-2 border-primary-500"
                        : "bg-white border border-gray-200 hover:bg-gray-50"
                    }`}
                    onClick={() =>
                      setUserChoices({ ...userChoices, breakfast: "beef-lamb" })
                    }
                  >
                    <span className="text-2xl mb-2">🥩</span>
                    <span className="font-medium">Beef/lamb-based</span>
                    <span className="text-sm text-gray-500 mt-1">
                      High carbon
                    </span>
                  </button>

                  <button
                    className={`p-4 rounded-md flex flex-col items-center ${
                      userChoices.breakfast === "chicken-eggs"
                        ? "bg-primary-100 border-2 border-primary-500"
                        : "bg-white border border-gray-200 hover:bg-gray-50"
                    }`}
                    onClick={() =>
                      setUserChoices({
                        ...userChoices,
                        breakfast: "chicken-eggs",
                      })
                    }
                  >
                    <span className="text-2xl mb-2">🍳</span>
                    <span className="font-medium">Chicken/eggs</span>
                    <span className="text-sm text-gray-500 mt-1">
                      Medium carbon
                    </span>
                  </button>

                  <button
                    className={`p-4 rounded-md flex flex-col items-center ${
                      userChoices.breakfast === "vegetarian"
                        ? "bg-primary-100 border-2 border-primary-500"
                        : "bg-white border border-gray-200 hover:bg-gray-50"
                    }`}
                    onClick={() =>
                      setUserChoices({
                        ...userChoices,
                        breakfast: "vegetarian",
                      })
                    }
                  >
                    <span className="text-2xl mb-2">🥗</span>
                    <span className="font-medium">Vegetarian</span>
                    <span className="text-sm text-gray-500 mt-1">
                      Low carbon
                    </span>
                  </button>
                </div>
              </div>

              {/* Commute Question */}
              <div className="p-4 bg-primary-50 rounded-lg">
                <h3 className="text-lg font-medium text-gray-800 mb-3 flex items-center">
                  <Car className="mr-2 h-5 w-5 text-primary-600" />
                  How do you usually commute?
                </h3>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-3">
                  <button
                    className={`p-4 rounded-md flex flex-col items-center ${
                      userChoices.commute === "drive-alone"
                        ? "bg-primary-100 border-2 border-primary-500"
                        : "bg-white border border-gray-200 hover:bg-gray-50"
                    }`}
                    onClick={() =>
                      setUserChoices({ ...userChoices, commute: "drive-alone" })
                    }
                  >
                    <span className="text-2xl mb-2">🚗</span>
                    <span className="font-medium">Drive alone</span>
                    <span className="text-sm text-gray-500 mt-1">
                      High emissions
                    </span>
                  </button>

                  <button
                    className={`p-4 rounded-md flex flex-col items-center ${
                      userChoices.commute === "carpool"
                        ? "bg-primary-100 border-2 border-primary-500"
                        : "bg-white border border-gray-200 hover:bg-gray-50"
                    }`}
                    onClick={() =>
                      setUserChoices({ ...userChoices, commute: "carpool" })
                    }
                  >
                    <span className="text-2xl mb-2">🚙</span>
                    <span className="font-medium">Carpool</span>
                    <span className="text-sm text-gray-500 mt-1">
                      Medium emissions
                    </span>
                  </button>

                  <button
                    className={`p-4 rounded-md flex flex-col items-center ${
                      userChoices.commute === "public-transport"
                        ? "bg-primary-100 border-2 border-primary-500"
                        : "bg-white border border-gray-200 hover:bg-gray-50"
                    }`}
                    onClick={() =>
                      setUserChoices({
                        ...userChoices,
                        commute: "public-transport",
                      })
                    }
                  >
                    <span className="text-2xl mb-2">🚇</span>
                    <span className="font-medium">Public transport</span>
                    <span className="text-sm text-gray-500 mt-1">
                      Lower emissions
                    </span>
                  </button>

                  <button
                    className={`p-4 rounded-md flex flex-col items-center ${
                      userChoices.commute === "walk-bike"
                        ? "bg-primary-100 border-2 border-primary-500"
                        : "bg-white border border-gray-200 hover:bg-gray-50"
                    }`}
                    onClick={() =>
                      setUserChoices({ ...userChoices, commute: "walk-bike" })
                    }
                  >
                    <span className="text-2xl mb-2">🚶</span>
                    <span className="font-medium">Walk/Bike</span>
                    <span className="text-sm text-gray-500 mt-1">
                      No emissions
                    </span>
                  </button>
                </div>
              </div>

              {/* Water Question */}
              <div className="p-4 bg-primary-50 rounded-lg">
                <h3 className="text-lg font-medium text-gray-800 mb-3 flex items-center">
                  <span className="mr-2 text-primary-600">💧</span>
                  Do you drink bottled water?
                </h3>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                  <button
                    className={`p-4 rounded-md flex flex-col items-center ${
                      userChoices.water === "bottled"
                        ? "bg-primary-100 border-2 border-primary-500"
                        : "bg-white border border-gray-200 hover:bg-gray-50"
                    }`}
                    onClick={() =>
                      setUserChoices({ ...userChoices, water: "bottled" })
                    }
                  >
                    <span className="text-2xl mb-2">🧴</span>
                    <span className="font-medium">Yes, daily</span>
                    <span className="text-sm text-gray-500 mt-1">
                      High plastic use
                    </span>
                  </button>

                  <button
                    className={`p-4 rounded-md flex flex-col items-center ${
                      userChoices.water === "occasionally"
                        ? "bg-primary-100 border-2 border-primary-500"
                        : "bg-white border border-gray-200 hover:bg-gray-50"
                    }`}
                    onClick={() =>
                      setUserChoices({ ...userChoices, water: "occasionally" })
                    }
                  >
                    <span className="text-2xl mb-2">🧴🍶</span>
                    <span className="font-medium">Occasionally</span>
                    <span className="text-sm text-gray-500 mt-1">
                      Mixed usage
                    </span>
                  </button>

                  <button
                    className={`p-4 rounded-md flex flex-col items-center ${
                      userChoices.water === "filter"
                        ? "bg-primary-100 border-2 border-primary-500"
                        : "bg-white border border-gray-200 hover:bg-gray-50"
                    }`}
                    onClick={() =>
                      setUserChoices({ ...userChoices, water: "filter" })
                    }
                  >
                    <span className="text-2xl mb-2">🍶</span>
                    <span className="font-medium">I use a filter/refill</span>
                    <span className="text-sm text-gray-500 mt-1">
                      Low impact
                    </span>
                  </button>
                </div>
              </div>

              {/* Lunch Question */}
              <div className="p-4 bg-primary-50 rounded-lg">
                <h3 className="text-lg font-medium text-gray-800 mb-3 flex items-center">
                  <span className="mr-2 text-primary-600">🍽️</span>
                  Lunch type?
                </h3>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                  <button
                    className={`p-4 rounded-md flex flex-col items-center ${
                      userChoices.lunch === "red-meat"
                        ? "bg-primary-100 border-2 border-primary-500"
                        : "bg-white border border-gray-200 hover:bg-gray-50"
                    }`}
                    onClick={() =>
                      setUserChoices({ ...userChoices, lunch: "red-meat" })
                    }
                  >
                    <span className="text-2xl mb-2">🥩</span>
                    <span className="font-medium">Red meat</span>
                    <span className="text-sm text-gray-500 mt-1">
                      High carbon
                    </span>
                  </button>

                  <button
                    className={`p-4 rounded-md flex flex-col items-center ${
                      userChoices.lunch === "chicken-fish"
                        ? "bg-primary-100 border-2 border-primary-500"
                        : "bg-white border border-gray-200 hover:bg-gray-50"
                    }`}
                    onClick={() =>
                      setUserChoices({ ...userChoices, lunch: "chicken-fish" })
                    }
                  >
                    <span className="text-2xl mb-2">🍗🐟</span>
                    <span className="font-medium">Chicken/fish</span>
                    <span className="text-sm text-gray-500 mt-1">
                      Medium carbon
                    </span>
                  </button>

                  <button
                    className={`p-4 rounded-md flex flex-col items-center ${
                      userChoices.lunch === "vegetarian"
                        ? "bg-primary-100 border-2 border-primary-500"
                        : "bg-white border border-gray-200 hover:bg-gray-50"
                    }`}
                    onClick={() =>
                      setUserChoices({ ...userChoices, lunch: "vegetarian" })
                    }
                  >
                    <span className="text-2xl mb-2">🥗</span>
                    <span className="font-medium">Vegetarian</span>
                    <span className="text-sm text-gray-500 mt-1">
                      Low carbon
                    </span>
                  </button>
                </div>
              </div>

              {/* Dinner Question */}
              <div className="p-4 bg-primary-50 rounded-lg">
                <h3 className="text-lg font-medium text-gray-800 mb-3 flex items-center">
                  <span className="mr-2 text-primary-600">🍲</span>
                  What do you usually eat for dinner?
                </h3>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                  <button
                    className={`p-4 rounded-md flex flex-col items-center ${
                      userChoices.dinner === "red-meat"
                        ? "bg-primary-100 border-2 border-primary-500"
                        : "bg-white border border-gray-200 hover:bg-gray-50"
                    }`}
                    onClick={() =>
                      setUserChoices({ ...userChoices, dinner: "red-meat" })
                    }
                  >
                    <span className="text-2xl mb-2">🥩</span>
                    <span className="font-medium">Red meat</span>
                    <span className="text-sm text-gray-500 mt-1">
                      High carbon
                    </span>
                  </button>

                  <button
                    className={`p-4 rounded-md flex flex-col items-center ${
                      userChoices.dinner === "chicken-fish"
                        ? "bg-primary-100 border-2 border-primary-500"
                        : "bg-white border border-gray-200 hover:bg-gray-50"
                    }`}
                    onClick={() =>
                      setUserChoices({ ...userChoices, dinner: "chicken-fish" })
                    }
                  >
                    <span className="text-2xl mb-2">🍗🐟</span>
                    <span className="font-medium">Chicken/fish</span>
                    <span className="text-sm text-gray-500 mt-1">
                      Medium carbon
                    </span>
                  </button>

                  <button
                    className={`p-4 rounded-md flex flex-col items-center ${
                      userChoices.dinner === "vegetarian"
                        ? "bg-primary-100 border-2 border-primary-500"
                        : "bg-white border border-gray-200 hover:bg-gray-50"
                    }`}
                    onClick={() =>
                      setUserChoices({ ...userChoices, dinner: "vegetarian" })
                    }
                  >
                    <span className="text-2xl mb-2">🥗</span>
                    <span className="font-medium">Vegetarian</span>
                    <span className="text-sm text-gray-500 mt-1">
                      Low carbon
                    </span>
                  </button>
                </div>
              </div>

              {/* Shopping Question */}
              <div className="p-4 bg-primary-50 rounded-lg">
                <h3 className="text-lg font-medium text-gray-800 mb-3 flex items-center">
                  <ShoppingBag className="mr-2 h-5 w-5 text-primary-600" />
                  How often do you shop for new clothes or electronics?
                </h3>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                  <button
                    className={`p-4 rounded-md flex flex-col items-center ${
                      userChoices.shopping === "weekly"
                        ? "bg-primary-100 border-2 border-primary-500"
                        : "bg-white border border-gray-200 hover:bg-gray-50"
                    }`}
                    onClick={() =>
                      setUserChoices({ ...userChoices, shopping: "weekly" })
                    }
                  >
                    <span className="text-2xl mb-2">🛍️🛍️</span>
                    <span className="font-medium">Weekly</span>
                    <span className="text-sm text-gray-500 mt-1">
                      Frequent shopper
                    </span>
                  </button>

                  <button
                    className={`p-4 rounded-md flex flex-col items-center ${
                      userChoices.shopping === "monthly"
                        ? "bg-primary-100 border-2 border-primary-500"
                        : "bg-white border border-gray-200 hover:bg-gray-50"
                    }`}
                    onClick={() =>
                      setUserChoices({ ...userChoices, shopping: "monthly" })
                    }
                  >
                    <span className="text-2xl mb-2">🛍️</span>
                    <span className="font-medium">Monthly</span>
                    <span className="text-sm text-gray-500 mt-1">
                      Moderate shopper
                    </span>
                  </button>

                  <button
                    className={`p-4 rounded-md flex flex-col items-center ${
                      userChoices.shopping === "rarely"
                        ? "bg-primary-100 border-2 border-primary-500"
                        : "bg-white border border-gray-200 hover:bg-gray-50"
                    }`}
                    onClick={() =>
                      setUserChoices({ ...userChoices, shopping: "rarely" })
                    }
                  >
                    <span className="text-2xl mb-2">👕</span>
                    <span className="font-medium">Rarely</span>
                    <span className="text-sm text-gray-500 mt-1">
                      Minimal shopper
                    </span>
                  </button>
                </div>
              </div>
            </div>

            <div className="mt-8 flex justify-between">
              <p className="text-sm text-gray-500 italic">
                Your selections will help calculate your daily carbon footprint.
              </p>
              <button
                onClick={() => {
                  handleSectionComplete(1);
                  setActiveSection(2);
                }}
                className="px-4 py-2 rounded-md bg-[#349383] text-white hover:bg-primary-700"
                disabled={Object.values(userChoices).filter(Boolean).length < 3}
              >
                See My Daily Emissions →
              </button>
            </div>
          </div>
        )}

        {activeSection === 2 && (
          <div className="bg-white shadow-md rounded-lg p-6">
            <h2 className="text-2xl font-semibold text-gray-800 mb-4">
              Your Daily Emissions
            </h2>

            <div className="grid grid-cols-1 lg:grid-cols-3 gap-8 mb-8">
              <div className="lg:col-span-2">
                <div className="mb-6">
                  <div className="flex items-center justify-between mb-2">
                    <h3 className="text-lg font-medium">
                      Your Estimated Daily Footprint:
                    </h3>
                    <span className="text-2xl font-bold text-primary-700">
                      {dailyFootprint.toFixed(1)} kg CO₂-eq
                    </span>
                  </div>

                  <div className="flex flex-col space-y-2">
                    <div className="flex justify-between text-sm">
                      <span>UAE Average: ~14 kg/day</span>
                      <span>Global Average: ~5 kg/day</span>
                    </div>
                  </div>

                  <p className="mt-4 text-gray-700">
                    Based on your answers, your footprint is about{" "}
                    {(dailyFootprint / 5).toFixed(1)}× the global average.
                    {dailyFootprint > 14
                      ? " That's higher than the UAE average - but you have several opportunities to reduce it."
                      : dailyFootprint > 5
                      ? " That's not unusual for life in the UAE — but it means small shifts can have a big impact."
                      : " That's excellent! You're already below both the UAE and global averages."}
                  </p>
                </div>

                <div className="bg-primary-50 p-4 rounded-lg">
                  <h3 className="font-medium mb-3">Your CO₂ Breakdown</h3>

                  {/* Replace the original breakdown with a pie chart */}
                  <div className="h-64 flex items-center justify-center">
                    {Object.keys(footprintBreakdown).length > 0 && (
                      <Pie
                        data={{
                          labels: Object.keys(footprintBreakdown).map(
                            (category) =>
                              category === "ac"
                                ? "Air Conditioning"
                                : category === "commute"
                                ? "Transportation"
                                : category.charAt(0).toUpperCase() +
                                  category.slice(1)
                          ),
                          datasets: [
                            {
                              data: Object.values(footprintBreakdown),
                              backgroundColor: [
                                "#4C8DAE", // AC - Blue
                                "#FF9F1C", // Breakfast - Orange
                                "#2EC4B6", // Commute - Teal
                                "#59A5D8", // Water - Light Blue
                                "#E76F51", // Lunch - Red
                                "#E09F3E", // Dinner - Gold
                                "#9B5DE5", // Shopping - Purple
                              ],
                              borderWidth: 1,
                            },
                          ],
                        }}
                        options={{
                          plugins: {
                            legend: {
                              position: "right",
                              labels: {
                                generateLabels: (chart) => {
                                  const data = chart.data;
                                  if (
                                    data.labels.length &&
                                    data.datasets.length
                                  ) {
                                    return data.labels.map((label, i) => {
                                      const value =
                                        data.datasets[0].data[i].toFixed(1);
                                      return {
                                        text: `${label}: ${value} kg`,
                                        fillStyle:
                                          data.datasets[0].backgroundColor[i],
                                        hidden: false,
                                        index: i,
                                      };
                                    });
                                  }
                                  return [];
                                },
                              },
                            },
                            tooltip: {
                              callbacks: {
                                label: (tooltipItem) => {
                                  const value = tooltipItem.raw;
                                  const percent = (
                                    (value / dailyFootprint) *
                                    100
                                  ).toFixed(1);
                                  return `${tooltipItem.label}: ${value.toFixed(
                                    1
                                  )} kg (${percent}%)`;
                                },
                              },
                            },
                          },
                          responsive: true,
                          maintainAspectRatio: false,
                        }}
                      />
                    )}
                  </div>
                </div>
              </div>

              <div className="bg-amber-50 p-4 rounded-lg">
                <h3 className="font-medium mb-4 text-amber-800">
                  Your Biggest Impact Areas
                </h3>

                {Object.entries(footprintBreakdown)
                  .sort(([, a], [, b]) => b - a)
                  .slice(0, 3)
                  .map(([category, value]) => {
                    const categoryName =
                      category === "ac"
                        ? "Air Conditioning"
                        : category === "commute"
                        ? "Transportation"
                        : category.charAt(0).toUpperCase() + category.slice(1);

                    const icon =
                      category === "ac"
                        ? "❄️"
                        : category === "commute"
                        ? "🚗"
                        : category === "breakfast" ||
                          category === "lunch" ||
                          category === "dinner"
                        ? "🍽️"
                        : category === "water"
                        ? "💧"
                        : category === "shopping"
                        ? "🛍️"
                        : "📊";

                    return (
                      <div
                        key={category}
                        className="mb-4 bg-white p-3 rounded-md shadow-sm"
                      >
                        <div className="flex items-center space-x-3">
                          <span className="text-2xl">{icon}</span>
                          <div>
                            <h4 className="font-medium">{categoryName}</h4>
                            <p className="text-sm text-gray-600">
                              {value.toFixed(1)} kg CO₂-eq/day
                            </p>
                          </div>
                        </div>
                      </div>
                    );
                  })}

                <div className="mt-6 p-3 bg-amber-100 rounded-md">
                  <p className="text-sm text-amber-800">
                    <span className="font-medium">Tip:</span> Focus on your top
                    3 sources first. Small changes in these areas will have the
                    biggest impact.
                  </p>
                </div>
              </div>
            </div>

            <div className="mt-8 border-t pt-6">
              <h3 className="text-xl font-medium mb-4">
                Your Choices in Detail
              </h3>

              <div className="space-y-4">
                {userChoices.ac && (
                  <div className="bg-white border rounded-md overflow-hidden">
                    <div className="flex flex-col md:flex-row">
                      <div className="bg-primary-50 p-4 md:w-1/3">
                        <div className="flex items-center mb-2">
                          <Thermometer className="h-5 w-5 text-primary-600 mr-2" />
                          <h4 className="font-medium">
                            AC Temperature: {userChoices.ac}
                          </h4>
                        </div>
                        <p className="text-sm text-gray-600">
                          You selected: {userChoices.ac}
                        </p>
                        <p className="text-sm font-medium text-primary-700 mt-2">
                          CO₂ impact: {emissionFactors.ac[userChoices.ac]}{" "}
                          kg/day
                        </p>
                      </div>

                      <div className="p-4 md:w-2/3">
                        <div className="grid grid-cols-2 gap-4">
                          <div>
                            <h5 className="font-medium text-gray-800 mb-1">
                              Your Choice
                            </h5>
                            <div className="flex items-center space-x-2">
                              <span>Temp</span>
                              <span className="font-medium">
                                {userChoices.ac}
                              </span>
                            </div>
                            <div className="flex items-center space-x-2 mt-1">
                              <span>CO₂</span>
                              <span className="font-medium">
                                {emissionFactors.ac[userChoices.ac]} kg/day
                              </span>
                            </div>
                            <div
                              className="mt-2 h-2 bg-primary-600 rounded-full"
                              style={{
                                width: `${
                                  emissionFactors.ac[userChoices.ac] * 20
                                }%`,
                              }}
                            ></div>
                          </div>

                          <div>
                            <h5 className="font-medium text-gray-800 mb-1">
                              Better Choice
                            </h5>
                            {userChoices.ac === "24-26°C" ||
                            userChoices.ac === "no-ac" ? (
                              <div>
                                <div className="flex items-center space-x-2 text-green-600">
                                  <span>✓</span>
                                  <span className="font-medium">
                                    You've made the best choice!
                                  </span>
                                </div>
                                <p className="text-sm text-green-600 mt-1">
                                  Your current choice is already optimal for the
                                  environment.
                                </p>
                              </div>
                            ) : (
                              <>
                                <div className="flex items-center space-x-2">
                                  <span>Temp</span>
                                  <span className="font-medium">
                                    24-26°C + Fan
                                  </span>
                                </div>
                                <div className="flex items-center space-x-2 mt-1">
                                  <span>CO₂</span>
                                  <span className="font-medium">
                                    {emissionFactors.ac["24-26°C"]} kg/day
                                  </span>
                                </div>
                                <div
                                  className="mt-2 h-2 bg-primary-600 rounded-full"
                                  style={{
                                    width: `${
                                      emissionFactors.ac["24-26°C"] * 20
                                    }%`,
                                  }}
                                ></div>
                              </>
                            )}
                          </div>
                        </div>

                        <div className="mt-4 text-sm bg-gray-50 p-2 rounded">
                          <span className="font-medium">What It Means: </span>
                          {userChoices.ac === "16-20°C" &&
                            "You use ~48 kg CO₂/month more than needed = driving 192 km or cutting down 25 trees"}
                          {userChoices.ac === "21-23°C" &&
                            "You use ~24 kg CO₂/month more than needed = driving 96 km"}
                          {userChoices.ac === "24-26°C" &&
                            "You're already using an efficient temperature setting!"}
                          {userChoices.ac === "no-ac" &&
                            "You're creating zero emissions from AC - excellent!"}
                        </div>
                      </div>
                    </div>
                  </div>
                )}

                {userChoices.commute && (
                  <div className="bg-white border rounded-md overflow-hidden">
                    <div className="flex flex-col md:flex-row">
                      <div className="bg-primary-50 p-4 md:w-1/3">
                        <div className="flex items-center mb-2">
                          <Car className="h-5 w-5 text-primary-600 mr-2" />
                          <h4 className="font-medium">
                            Commute: {userChoices.commute.replace("-", " ")}
                          </h4>
                        </div>
                        <p className="text-sm text-gray-600">
                          You selected: {userChoices.commute.replace("-", " ")}
                        </p>
                        <p className="text-sm font-medium text-primary-700 mt-2">
                          CO₂ impact:{" "}
                          {emissionFactors.commute[userChoices.commute]} kg/day
                        </p>
                      </div>

                      <div className="p-4 md:w-2/3">
                        <div className="grid grid-cols-2 gap-4">
                          <div>
                            <h5 className="font-medium text-gray-800 mb-1">
                              Your Choice
                            </h5>
                            <div className="flex items-center space-x-2">
                              <span>Mode</span>
                              <span className="font-medium">
                                {userChoices.commute.replace("-", " ")}
                              </span>
                            </div>
                            <div className="flex items-center space-x-2 mt-1">
                              <span>CO₂</span>
                              <span className="font-medium">
                                {emissionFactors.commute[userChoices.commute]}{" "}
                                kg/day
                              </span>
                            </div>
                            <div
                              className="mt-2 h-2 bg-primary-600 rounded-full"
                              style={{
                                width: `${
                                  emissionFactors.commute[userChoices.commute] *
                                  20
                                }%`,
                              }}
                            ></div>
                          </div>

                          <div>
                            <h5 className="font-medium text-gray-800 mb-1">
                              Better Choice
                            </h5>
                            {userChoices.commute === "walk-bike" ? (
                              <div>
                                <div className="flex items-center space-x-2 text-green-600">
                                  <span>✓</span>
                                  <span className="font-medium">
                                    You've made the best choice!
                                  </span>
                                </div>
                                <p className="text-sm text-green-600 mt-1">
                                  Walking/biking produces zero emissions -
                                  excellent!
                                </p>
                              </div>
                            ) : (
                              <>
                                <div className="flex items-center space-x-2">
                                  <span>Mode</span>
                                  <span className="font-medium">
                                    {userChoices.commute === "public-transport"
                                      ? "Walking/Biking"
                                      : "Public transport"}
                                  </span>
                                </div>
                                <div className="flex items-center space-x-2 mt-1">
                                  <span>CO₂</span>
                                  <span className="font-medium">
                                    {userChoices.commute === "public-transport"
                                      ? "0"
                                      : "0.7"}{" "}
                                    kg/day
                                  </span>
                                </div>
                                <div
                                  className="mt-2 h-2 bg-primary-600 rounded-full"
                                  style={{
                                    width: `${
                                      (userChoices.commute ===
                                      "public-transport"
                                        ? 0
                                        : 0.7) * 20
                                    }%`,
                                  }}
                                ></div>
                              </>
                            )}
                          </div>
                        </div>

                        <div className="mt-4 text-sm bg-gray-50 p-2 rounded">
                          <span className="font-medium">What It Means: </span>
                          {userChoices.commute === "drive-alone" &&
                            "That's 114 kg CO₂/month — more than your fridge emits all year"}
                          {userChoices.commute === "carpool" &&
                            "You're already reducing emissions by sharing rides!"}
                          {userChoices.commute === "public-transport" &&
                            "You're making a great low-carbon choice for commuting!"}
                          {userChoices.commute === "walk-bike" &&
                            "Your commute creates zero emissions - that's excellent!"}
                        </div>
                      </div>
                    </div>
                  </div>
                )}

                {/* Breakfast Choice */}
                {userChoices.breakfast && (
                  <div className="bg-white border rounded-md overflow-hidden">
                    <div className="flex flex-col md:flex-row">
                      <div className="bg-primary-50 p-4 md:w-1/3">
                        <div className="flex items-center mb-2">
                          <Coffee className="h-5 w-5 text-primary-600 mr-2" />
                          <h4 className="font-medium">
                            Breakfast: {userChoices.breakfast.replace("-", "/")}
                          </h4>
                        </div>
                        <p className="text-sm text-gray-600">
                          You selected:{" "}
                          {userChoices.breakfast.replace("-", "/")}
                        </p>
                        <p className="text-sm font-medium text-primary-700 mt-2">
                          CO₂ impact:{" "}
                          {emissionFactors.breakfast[userChoices.breakfast]}{" "}
                          kg/day
                        </p>
                      </div>

                      <div className="p-4 md:w-2/3">
                        <div className="grid grid-cols-2 gap-4">
                          <div>
                            <h5 className="font-medium text-gray-800 mb-1">
                              Your Choice
                            </h5>
                            <div className="flex items-center space-x-2">
                              <span>Type</span>
                              <span className="font-medium">
                                {userChoices.breakfast.replace("-", "/")}
                              </span>
                            </div>
                            <div className="flex items-center space-x-2 mt-1">
                              <span>CO₂</span>
                              <span className="font-medium">
                                {
                                  emissionFactors.breakfast[
                                    userChoices.breakfast
                                  ]
                                }{" "}
                                kg/day
                              </span>
                            </div>
                            <div
                              className="mt-2 h-2 bg-primary-600 rounded-full"
                              style={{
                                width: `${
                                  emissionFactors.breakfast[
                                    userChoices.breakfast
                                  ] * 20
                                }%`,
                              }}
                            ></div>
                          </div>

                          <div>
                            <h5 className="font-medium text-gray-800 mb-1">
                              Better Choice
                            </h5>
                            {userChoices.breakfast === "vegetarian" ? (
                              <div>
                                <div className="flex items-center space-x-2 text-green-600">
                                  <span>✓</span>
                                  <span className="font-medium">
                                    You've made the best choice!
                                  </span>
                                </div>
                                <p className="text-sm text-green-600 mt-1">
                                  Vegetarian breakfast has the lowest carbon
                                  footprint.
                                </p>
                              </div>
                            ) : (
                              <>
                                <div className="flex items-center space-x-2">
                                  <span>Type</span>
                                  <span className="font-medium">
                                    Vegetarian
                                  </span>
                                </div>
                                <div className="flex items-center space-x-2 mt-1">
                                  <span>CO₂</span>
                                  <span className="font-medium">
                                    {emissionFactors.breakfast["vegetarian"]}{" "}
                                    kg/day
                                  </span>
                                </div>
                                <div
                                  className="mt-2 h-2 bg-primary-600 rounded-full"
                                  style={{
                                    width: `${
                                      emissionFactors.breakfast["vegetarian"] *
                                      20
                                    }%`,
                                  }}
                                ></div>
                              </>
                            )}
                          </div>
                        </div>

                        <div className="mt-4 text-sm bg-gray-50 p-2 rounded">
                          <span className="font-medium">What It Means: </span>
                          {userChoices.breakfast === "beef-lamb" &&
                            "A beef breakfast has 10x higher carbon footprint than a vegetarian one. That's equivalent to driving 14 km extra."}
                          {userChoices.breakfast === "chicken-eggs" &&
                            "Eggs and chicken have a medium impact. Still 5x higher than plant-based options."}
                          {userChoices.breakfast === "vegetarian" &&
                            "You're making an excellent low-carbon choice for your breakfast!"}
                        </div>
                      </div>
                    </div>
                  </div>
                )}

                {/* Water Usage */}
                {userChoices.water && (
                  <div className="bg-white border rounded-md overflow-hidden">
                    <div className="flex flex-col md:flex-row">
                      <div className="bg-primary-50 p-4 md:w-1/3">
                        <div className="flex items-center mb-2">
                          <span className="text-primary-600 mr-2">💧</span>
                          <h4 className="font-medium">
                            Water: {userChoices.water}
                          </h4>
                        </div>
                        <p className="text-sm text-gray-600">
                          You selected: {userChoices.water}
                        </p>
                        <p className="text-sm font-medium text-primary-700 mt-2">
                          CO₂ impact: {emissionFactors.water[userChoices.water]}{" "}
                          kg/day
                        </p>
                      </div>

                      <div className="p-4 md:w-2/3">
                        <div className="grid grid-cols-2 gap-4">
                          <div>
                            <h5 className="font-medium text-gray-800 mb-1">
                              Your Choice
                            </h5>
                            <div className="flex items-center space-x-2">
                              <span>Type</span>
                              <span className="font-medium">
                                {userChoices.water}
                              </span>
                            </div>
                            <div className="flex items-center space-x-2 mt-1">
                              <span>CO₂</span>
                              <span className="font-medium">
                                {emissionFactors.water[userChoices.water]}{" "}
                                kg/day
                              </span>
                            </div>
                            <div
                              className="mt-2 h-2 bg-primary-600 rounded-full"
                              style={{
                                width: `${
                                  emissionFactors.water[userChoices.water] * 100
                                }%`,
                              }}
                            ></div>
                          </div>

                          <div>
                            <h5 className="font-medium text-gray-800 mb-1">
                              Better Choice
                            </h5>
                            {userChoices.water === "filter" ? (
                              <div>
                                <div className="flex items-center space-x-2 text-green-600">
                                  <span>✓</span>
                                  <span className="font-medium">
                                    You've made the best choice!
                                  </span>
                                </div>
                                <p className="text-sm text-green-600 mt-1">
                                  Filtered water has the lowest carbon
                                  footprint.
                                </p>
                              </div>
                            ) : (
                              <>
                                <div className="flex items-center space-x-2">
                                  <span>Type</span>
                                  <span className="font-medium">Filtered</span>
                                </div>
                                <div className="flex items-center space-x-2 mt-1">
                                  <span>CO₂</span>
                                  <span className="font-medium">
                                    {emissionFactors.water["filter"]} kg/day
                                  </span>
                                </div>
                                <div
                                  className="mt-2 h-2 bg-primary-600 rounded-full"
                                  style={{
                                    width: `${
                                      emissionFactors.water["filter"] * 100
                                    }%`,
                                  }}
                                ></div>
                              </>
                            )}
                          </div>
                        </div>

                        <div className="mt-4 text-sm bg-gray-50 p-2 rounded">
                          <span className="font-medium">What It Means: </span>
                          {userChoices.water === "bottled" &&
                            "Bottled water has 20x the carbon footprint of filtered water. That's 73 kg CO₂ annually just for drinking water!"}
                          {userChoices.water === "occasionally" &&
                            "Occasional bottled water use still adds up. Consider a good filter to eliminate this source."}
                          {userChoices.water === "filter" &&
                            "You're making an excellent choice with filtered water!"}
                        </div>
                      </div>
                    </div>
                  </div>
                )}

                {/* Lunch Choice */}
                {userChoices.lunch && (
                  <div className="bg-white border rounded-md overflow-hidden">
                    <div className="flex flex-col md:flex-row">
                      <div className="bg-primary-50 p-4 md:w-1/3">
                        <div className="flex items-center mb-2">
                          <span className="text-primary-600 mr-2">🍽️</span>
                          <h4 className="font-medium">
                            Lunch: {userChoices.lunch.replace("-", "/")}
                          </h4>
                        </div>
                        <p className="text-sm text-gray-600">
                          You selected: {userChoices.lunch.replace("-", "/")}
                        </p>
                        <p className="text-sm font-medium text-primary-700 mt-2">
                          CO₂ impact: {emissionFactors.lunch[userChoices.lunch]}{" "}
                          kg/day
                        </p>
                      </div>

                      <div className="p-4 md:w-2/3">
                        <div className="grid grid-cols-2 gap-4">
                          <div>
                            <h5 className="font-medium text-gray-800 mb-1">
                              Your Choice
                            </h5>
                            <div className="flex items-center space-x-2">
                              <span>Type</span>
                              <span className="font-medium">
                                {userChoices.lunch.replace("-", "/")}
                              </span>
                            </div>
                            <div className="flex items-center space-x-2 mt-1">
                              <span>CO₂</span>
                              <span className="font-medium">
                                {emissionFactors.lunch[userChoices.lunch]}{" "}
                                kg/day
                              </span>
                            </div>
                            <div
                              className="mt-2 h-2 bg-primary-600 rounded-full"
                              style={{
                                width: `${
                                  emissionFactors.lunch[userChoices.lunch] * 10
                                }%`,
                              }}
                            ></div>
                          </div>

                          <div>
                            <h5 className="font-medium text-gray-800 mb-1">
                              Better Choice
                            </h5>
                            {userChoices.lunch === "vegetarian" ? (
                              <div>
                                <div className="flex items-center space-x-2 text-green-600">
                                  <span>✓</span>
                                  <span className="font-medium">
                                    You've made the best choice!
                                  </span>
                                </div>
                                <p className="text-sm text-green-600 mt-1">
                                  Vegetarian lunch has the lowest carbon
                                  footprint.
                                </p>
                              </div>
                            ) : (
                              <>
                                <div className="flex items-center space-x-2">
                                  <span>Type</span>
                                  <span className="font-medium">
                                    Vegetarian
                                  </span>
                                </div>
                                <div className="flex items-center space-x-2 mt-1">
                                  <span>CO₂</span>
                                  <span className="font-medium">
                                    {emissionFactors.lunch["vegetarian"]} kg/day
                                  </span>
                                </div>
                                <div
                                  className="mt-2 h-2 bg-primary-600 rounded-full"
                                  style={{
                                    width: `${
                                      emissionFactors.lunch["vegetarian"] * 10
                                    }%`,
                                  }}
                                ></div>
                              </>
                            )}
                          </div>
                        </div>

                        <div className="mt-4 text-sm bg-gray-50 p-2 rounded">
                          <span className="font-medium">What It Means: </span>
                          {userChoices.lunch === "red-meat" &&
                            "A red meat lunch emits 7x more CO₂ than a vegetarian one. That's equivalent to driving 20 km."}
                          {userChoices.lunch === "chicken-fish" &&
                            "Chicken and fish have medium impact, about 3x higher than vegetarian options."}
                          {userChoices.lunch === "vegetarian" &&
                            "You're making an excellent low-carbon choice for lunch!"}
                        </div>
                      </div>
                    </div>
                  </div>
                )}

                {/* Dinner Choice */}
                {userChoices.dinner && (
                  <div className="bg-white border rounded-md overflow-hidden">
                    <div className="flex flex-col md:flex-row">
                      <div className="bg-primary-50 p-4 md:w-1/3">
                        <div className="flex items-center mb-2">
                          <span className="text-primary-600 mr-2">🍲</span>
                          <h4 className="font-medium">
                            Dinner: {userChoices.dinner.replace("-", "/")}
                          </h4>
                        </div>
                        <p className="text-sm text-gray-600">
                          You selected: {userChoices.dinner.replace("-", "/")}
                        </p>
                        <p className="text-sm font-medium text-primary-700 mt-2">
                          CO₂ impact:{" "}
                          {emissionFactors.dinner[userChoices.dinner]} kg/day
                        </p>
                      </div>

                      <div className="p-4 md:w-2/3">
                        <div className="grid grid-cols-2 gap-4">
                          <div>
                            <h5 className="font-medium text-gray-800 mb-1">
                              Your Choice
                            </h5>
                            <div className="flex items-center space-x-2">
                              <span>Type</span>
                              <span className="font-medium">
                                {userChoices.dinner.replace("-", "/")}
                              </span>
                            </div>
                            <div className="flex items-center space-x-2 mt-1">
                              <span>CO₂</span>
                              <span className="font-medium">
                                {emissionFactors.dinner[userChoices.dinner]}{" "}
                                kg/day
                              </span>
                            </div>
                            <div
                              className="mt-2 h-2 bg-primary-600 rounded-full"
                              style={{
                                width: `${
                                  emissionFactors.dinner[userChoices.dinner] *
                                  10
                                }%`,
                              }}
                            ></div>
                          </div>

                          <div>
                            <h5 className="font-medium text-gray-800 mb-1">
                              Better Choice
                            </h5>
                            {userChoices.dinner === "vegetarian" ? (
                              <div>
                                <div className="flex items-center space-x-2 text-green-600">
                                  <span>✓</span>
                                  <span className="font-medium">
                                    You've made the best choice!
                                  </span>
                                </div>
                                <p className="text-sm text-green-600 mt-1">
                                  Vegetarian dinner has the lowest carbon
                                  footprint.
                                </p>
                              </div>
                            ) : (
                              <>
                                <div className="flex items-center space-x-2">
                                  <span>Type</span>
                                  <span className="font-medium">
                                    Vegetarian
                                  </span>
                                </div>
                                <div className="flex items-center space-x-2 mt-1">
                                  <span>CO₂</span>
                                  <span className="font-medium">
                                    {emissionFactors.dinner["vegetarian"]}{" "}
                                    kg/day
                                  </span>
                                </div>
                                <div
                                  className="mt-2 h-2 bg-primary-600 rounded-full"
                                  style={{
                                    width: `${
                                      emissionFactors.dinner["vegetarian"] * 10
                                    }%`,
                                  }}
                                ></div>
                              </>
                            )}
                          </div>
                        </div>

                        <div className="mt-4 text-sm bg-gray-50 p-2 rounded">
                          <span className="font-medium">What It Means: </span>
                          {userChoices.dinner === "red-meat" &&
                            "A red meat dinner emits 7x more CO₂ than a vegetarian one. That's equivalent to driving 20 km."}
                          {userChoices.dinner === "chicken-fish" &&
                            "Chicken and fish have medium impact, about 3x higher than vegetarian options."}
                          {userChoices.dinner === "vegetarian" &&
                            "You're making an excellent low-carbon choice for dinner!"}
                        </div>
                      </div>
                    </div>
                  </div>
                )}

                {/* Shopping Frequency */}
                {userChoices.shopping && (
                  <div className="bg-white border rounded-md overflow-hidden">
                    <div className="flex flex-col md:flex-row">
                      <div className="bg-primary-50 p-4 md:w-1/3">
                        <div className="flex items-center mb-2">
                          <ShoppingBag className="h-5 w-5 text-primary-600 mr-2" />
                          <h4 className="font-medium">
                            Shopping: {userChoices.shopping}
                          </h4>
                        </div>
                        <p className="text-sm text-gray-600">
                          You selected: {userChoices.shopping}
                        </p>
                        <p className="text-sm font-medium text-primary-700 mt-2">
                          CO₂ impact:{" "}
                          {emissionFactors.shopping[userChoices.shopping]}{" "}
                          kg/day
                        </p>
                      </div>

                      <div className="p-4 md:w-2/3">
                        <div className="grid grid-cols-2 gap-4">
                          <div>
                            <h5 className="font-medium text-gray-800 mb-1">
                              Your Choice
                            </h5>
                            <div className="flex items-center space-x-2">
                              <span>Frequency</span>
                              <span className="font-medium">
                                {userChoices.shopping}
                              </span>
                            </div>
                            <div className="flex items-center space-x-2 mt-1">
                              <span>CO₂</span>
                              <span className="font-medium">
                                {emissionFactors.shopping[userChoices.shopping]}{" "}
                                kg/day
                              </span>
                            </div>
                            <div
                              className="mt-2 h-2 bg-primary-600 rounded-full"
                              style={{
                                width: `${
                                  emissionFactors.shopping[
                                    userChoices.shopping
                                  ] * 30
                                }%`,
                              }}
                            ></div>
                          </div>

                          <div>
                            <h5 className="font-medium text-gray-800 mb-1">
                              Better Choice
                            </h5>
                            {userChoices.shopping === "rarely" ? (
                              <div>
                                <div className="flex items-center space-x-2 text-green-600">
                                  <span>✓</span>
                                  <span className="font-medium">
                                    You've made the best choice!
                                  </span>
                                </div>
                                <p className="text-sm text-green-600 mt-1">
                                  Shopping less frequently has the lowest carbon
                                  footprint.
                                </p>
                              </div>
                            ) : (
                              <>
                                <div className="flex items-center space-x-2">
                                  <span>Frequency</span>
                                  <span className="font-medium">Rarely</span>
                                </div>
                                <div className="flex items-center space-x-2 mt-1">
                                  <span>CO₂</span>
                                  <span className="font-medium">
                                    {emissionFactors.shopping["rarely"]} kg/day
                                  </span>
                                </div>
                                <div
                                  className="mt-2 h-2 bg-primary-600 rounded-full"
                                  style={{
                                    width: `${
                                      emissionFactors.shopping["rarely"] * 30
                                    }%`,
                                  }}
                                ></div>
                              </>
                            )}
                          </div>
                        </div>

                        <div className="mt-4 text-sm bg-gray-50 p-2 rounded">
                          <span className="font-medium">What It Means: </span>
                          {userChoices.shopping === "weekly" &&
                            "Frequent shopping for new goods has a high carbon cost - 20x more than occasional shopping."}
                          {userChoices.shopping === "monthly" &&
                            "Monthly shopping creates moderate emissions - try extending the life of your items."}
                          {userChoices.shopping === "rarely" &&
                            "You're making excellent choices by shopping less frequently!"}
                        </div>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            </div>

            <div className="mt-8 flex justify-between">
              <button
                onClick={() => setActiveSection(1)}
                className="px-4 py-2 rounded-md border border-primary-500 text-primary-500 hover:bg-primary-50"
              >
                ← Back to Your Choices
              </button>
              <button
                onClick={() => {
                  handleSectionComplete(2);
                  setActiveSection(3);
                }}
                className="px-4 py-2 rounded-md bg-[#349383] text-white hover:bg-primary-700"
              >
                UAE Emissions Deep-Dive →
              </button>
            </div>
          </div>
        )}

        {activeSection === 3 && (
          <div className="bg-white shadow-md rounded-lg p-6">
            <h2 className="text-2xl font-semibold text-gray-800 mb-4">
              UAE Emissions Deep-Dive
            </h2>

            <p className="text-gray-700 mb-6">
              The top sources of carbon emissions in the UAE are related to
              cooling, transportation, water consumption, and food choices.
              Understanding these emission sources helps target the most
              impactful areas for reduction.
            </p>

            {/* Horizontally scrollable cards */}
            <div className="flex overflow-x-auto pb-4 space-x-4 mb-8">
              <div className="min-w-[280px] bg-white border rounded-lg shadow-sm flex-shrink-0">
                <div className="p-4">
                  <div className="text-3xl mb-2">❄️</div>
                  <h3 className="font-bold text-lg mb-1">Cooling</h3>
                  <p className="text-sm text-gray-700 mb-2">
                    Up to 70% of your home's electricity
                  </p>
                  <p className="text-xs text-primary-600 font-medium">
                    Just 2°C colder = double emissions
                  </p>
                  <button
                    onClick={() => {
                      const element =
                        document.getElementById("cooling-article");
                      element?.scrollIntoView({ behavior: "smooth" });
                    }}
                    className="mt-4 text-primary-600 text-sm font-medium hover:underline"
                  >
                    Learn More →
                  </button>
                </div>
              </div>

              <div className="min-w-[280px] bg-white border rounded-lg shadow-sm flex-shrink-0">
                <div className="p-4">
                  <div className="text-3xl mb-2">🚗</div>
                  <h3 className="font-bold text-lg mb-1">Commute</h3>
                  <p className="text-sm text-gray-700 mb-2">
                    3.8 kg/day solo vs 1.2 kg/day carpool
                  </p>
                  <p className="text-xs text-primary-600 font-medium">
                    UAE commutes are ~2.4× global avg
                  </p>
                  <button
                    onClick={() => {
                      const element = document.getElementById("car-article");
                      element?.scrollIntoView({ behavior: "smooth" });
                    }}
                    className="mt-4 text-primary-600 text-sm font-medium hover:underline"
                  >
                    Learn More →
                  </button>
                </div>
              </div>

              <div className="min-w-[280px] bg-white border rounded-lg shadow-sm flex-shrink-0">
                <div className="p-4">
                  <div className="text-3xl mb-2">🧴</div>
                  <h3 className="font-bold text-lg mb-1">Bottled Water</h3>
                  <p className="text-sm text-gray-700 mb-2">
                    41 kg/year from 500 bottles
                  </p>
                  <p className="text-xs text-primary-600 font-medium">
                    = emissions of 2 full trees/year
                  </p>
                  <button
                    onClick={() => {
                      const element = document.getElementById("water-article");
                      element?.scrollIntoView({ behavior: "smooth" });
                    }}
                    className="mt-4 text-primary-600 text-sm font-medium hover:underline"
                  >
                    Learn More →
                  </button>
                </div>
              </div>

              <div className="min-w-[280px] bg-white border rounded-lg shadow-sm flex-shrink-0">
                <div className="p-4">
                  <div className="text-3xl mb-2">🍽️</div>
                  <h3 className="font-bold text-lg mb-1">Food</h3>
                  <p className="text-sm text-gray-700 mb-2">
                    1 kg beef = 60× lentils
                  </p>
                  <p className="text-xs text-primary-600 font-medium">
                    Type matters more than "local"
                  </p>
                  <button
                    onClick={() => {
                      const element = document.getElementById("food-article");
                      element?.scrollIntoView({ behavior: "smooth" });
                    }}
                    className="mt-4 text-primary-600 text-sm font-medium hover:underline"
                  >
                    Learn More →
                  </button>
                </div>
              </div>
            </div>

            {/* Replace bar visualization with radar chart */}
            <div className="flex gap-6 mb-10">
              <div className="w-1/2 bg-primary-50 p-5 rounded-lg">
                <h3 className="font-medium text-lg mb-4">
                  UAE vs Global Comparison
                </h3>

                <table className="min-w-full text-sm">
                  <thead>
                    <tr>
                      <th className="text-left pb-2">Category</th>
                      <th className="text-center pb-2">UAE (kg CO₂/day)</th>
                      <th className="text-center pb-2">Global (kg CO₂/day)</th>
                      <th className="text-center pb-2">Difference</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr className="border-t">
                      <td className="py-2">Cooling</td>
                      <td className="text-center">5.5</td>
                      <td className="text-center">1.2</td>
                      <td className="text-center font-medium text-red-600">
                        +4.6×
                      </td>
                    </tr>
                    <tr className="border-t">
                      <td className="py-2">Transportation</td>
                      <td className="text-center">4.2</td>
                      <td className="text-center">1.6</td>
                      <td className="text-center font-medium text-red-600">
                        +2.6×
                      </td>
                    </tr>
                    <tr className="border-t">
                      <td className="py-2">Food</td>
                      <td className="text-center">3.1</td>
                      <td className="text-center">1.8</td>
                      <td className="text-center font-medium text-red-600">
                        +1.7×
                      </td>
                    </tr>
                    <tr className="border-t font-medium">
                      <td className="py-2">Total Daily</td>
                      <td className="text-center">~14</td>
                      <td className="text-center">~5</td>
                      <td className="text-center font-medium text-red-600">
                        +2.8×
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>

              <div className="w-1/2 bg-amber-50 p-5 rounded-lg">
                <h3 className="font-medium mb-3">
                  Key Insights for UAE Residents
                </h3>
                <ul className="space-y-2">
                  <li className="flex items-start">
                    <div className="text-amber-600 mr-2">•</div>
                    <p className="text-sm">
                      The hot climate creates a necessary but energy-intensive
                      cooling demand
                    </p>
                  </li>
                  <li className="flex items-start">
                    <div className="text-amber-600 mr-2">•</div>
                    <p className="text-sm">
                      Car-centric infrastructure leads to higher transportation
                      emissions
                    </p>
                  </li>
                  <li className="flex items-start">
                    <div className="text-amber-600 mr-2">•</div>
                    <p className="text-sm">
                      Bottled water consumption is significantly higher than
                      global average
                    </p>
                  </li>
                  <li className="flex items-start">
                    <div className="text-amber-600 mr-2">•</div>
                    <p className="text-sm">
                      Diet choices have outsized impact in a country importing
                      90% of food
                    </p>
                  </li>
                  <li className="flex items-start">
                    <div className="text-amber-600 mr-2">•</div>
                    <p className="text-sm">
                      Small shifts in these areas can yield major carbon
                      reductions
                    </p>
                  </li>
                </ul>
              </div>
            </div>

            {/* Deep Dive Articles - merged from section 4 */}
            <div className="space-y-8">
              {/* Cooling Article */}
              <div className="border-b pb-8" id="cooling-article">
                <div className="flex items-center mb-4">
                  <div className="bg-blue-100 p-2 rounded-full mr-3">
                    <Thermometer className="h-6 w-6 text-blue-600" />
                  </div>
                  <h3 className="text-xl font-medium">
                    Cooling Culture: The Invisible Giant
                  </h3>
                </div>

                <div className="prose max-w-none">
                  <p>
                    In the UAE, AC isn't a luxury — it's survival. But setting
                    your thermostat to 16°C instead of 24°C? That's not survival
                    — that's excess.
                  </p>

                  <ul className="mt-4 space-y-2 list-disc pl-5">
                    <li>
                      AC is the single biggest electricity draw in households (≈
                      70%)
                    </li>
                    <li>Every degree below 24°C boosts energy use by ~7–10%</li>
                    <li>
                      At 16°C, your flat can emit 3.6 kg CO₂/day just for
                      cooling
                    </li>
                    <li>At 24°C: around 1.7 kg/day</li>
                    <li>
                      In one month, that's an extra 60 kg CO₂ — equal to what 30
                      trees absorb monthly
                    </li>
                  </ul>

                  <div className="bg-blue-50 p-4 rounded-lg mt-4">
                    <h4 className="font-medium text-blue-800 mb-2">
                      What you can do:
                    </h4>
                    <p className="text-blue-700">
                      Set your thermostat to 24–25°C and add a ceiling fan. Same
                      comfort, less energy, less guilt. And a smaller DEWA bill.
                    </p>
                  </div>
                </div>
              </div>

              {/* Car Culture Article */}
              <div className="border-b pb-8" id="car-article">
                <div className="flex items-center mb-4">
                  <div className="bg-green-100 p-2 rounded-full mr-3">
                    <Car className="h-6 w-6 text-green-600" />
                  </div>
                  <h3 className="text-xl font-medium">
                    Car Culture: Our Daily 3.8 kg Mistake
                  </h3>
                </div>

                <div className="prose max-w-none">
                  <p>
                    Driving 20 km alone to work every day? It's like eating a
                    beef burger for breakfast — every single morning.
                  </p>

                  <ul className="mt-4 space-y-2 list-disc pl-5">
                    <li>
                      UAE: 74% drive alone. Metro exists, but it's underused.
                    </li>
                    <li>A 20-km round-trip = 3.8 kg CO₂/day = 114 kg/month</li>
                    <li>
                      Compare:
                      <ul className="mt-2 ml-6 space-y-1">
                        <li>Carpool with 2 others → ~1.3 kg/day</li>
                        <li>Metro → ~0.7 kg/day</li>
                      </ul>
                    </li>
                    <li>The global average for daily commute: ~1.6 kg/day</li>
                    <li>UAE residents emit 2–2.5× more from commuting</li>
                  </ul>

                  <div className="bg-green-50 p-4 rounded-lg mt-4">
                    <h4 className="font-medium text-green-800 mb-2">
                      What you can do:
                    </h4>
                    <p className="text-green-700">
                      Even 1 carpool day/week cuts ~15 kg/month. That's like
                      skipping 3 beef meals/month.
                    </p>
                  </div>
                </div>
              </div>

              {/* Bottled Water Article */}
              <div className="border-b pb-8" id="water-article">
                <div className="flex items-center mb-4">
                  <div className="bg-cyan-100 p-2 rounded-full mr-3">
                    <span className="text-xl text-cyan-600">💧</span>
                  </div>
                  <h3 className="text-xl font-medium">
                    Bottled Water: Small Sip, Big Problem
                  </h3>
                </div>

                <div className="prose max-w-none">
                  <p>
                    Bottled water feels light — but by the time it hits your
                    fridge, it's heavier than you think.
                  </p>

                  <ul className="mt-4 space-y-2 list-disc pl-5">
                    <li>One 1-L bottle: ~0.082 kg CO₂</li>
                    <li>
                      500/year = 41 kg CO₂/year =
                      <ul className="mt-2 ml-6 space-y-1">
                        <li>🌳 emissions 2 trees absorb annually</li>
                        <li>🚗 160 km of car travel</li>
                      </ul>
                    </li>
                    <li>
                      Problem isn't just plastic, but chilled storage, bottling
                      & shipping
                    </li>
                  </ul>

                  <div className="bg-cyan-50 p-4 rounded-lg mt-4">
                    <h4 className="font-medium text-cyan-800 mb-2">
                      What you can do:
                    </h4>
                    <p className="text-cyan-700">
                      Get a filter. RO or UV units last years, cost less over
                      time, and can cut this source to near-zero.
                    </p>
                  </div>
                </div>
              </div>

              {/* Food Article */}
              <div id="food-article">
                <div className="flex items-center mb-4">
                  <div className="bg-amber-100 p-2 rounded-full mr-3">
                    <span className="text-xl text-amber-600">🍽️</span>
                  </div>
                  <h3 className="text-xl font-medium">
                    Food: Don't Just Think Local — Think Type
                  </h3>
                </div>

                <div className="prose max-w-none">
                  <p>
                    Yes, it's great to eat local. But swapping beef for lentils
                    saves 60× more than swapping imported for local lettuce.
                  </p>

                  <ul className="mt-4 space-y-2 list-disc pl-5">
                    <li>UAE imports 90% of its food</li>
                    <li>
                      But the type matters most
                      <ul className="mt-2 ml-6 space-y-1">
                        <li>🥩 Beef = 60 kg CO₂e/kg</li>
                        <li>🐓 Chicken (local) = 6–7 kg CO₂e/kg</li>
                        <li>🫘 Lentils = 0.9 kg CO₂e/kg</li>
                        <li>🥦 Local seasonal veg = {"<"} 1 kg CO₂e/kg</li>
                      </ul>
                    </li>
                    <li>
                      Even air-freighted vegetables = ~1.5–2 kg CO₂ (still lower
                      than meat)
                    </li>
                  </ul>

                  <div className="bg-amber-50 p-4 rounded-lg mt-4">
                    <h4 className="font-medium text-amber-800 mb-2">
                      What you can do:
                    </h4>
                    <p className="text-amber-700">
                      Replace 3 red meat meals/week with local veg, lentils, or
                      chicken. That saves ~500–700 kg CO₂/year.
                    </p>
                  </div>
                </div>
              </div>
            </div>

            {/* Cooling Deep Dive Quick Read */}
            <div className="mt-10 bg-gray-50 p-5 rounded-lg">
              <h3 className="text-lg font-medium mb-4">
                Cooling: the silent heavyweight
              </h3>
              <p className="text-gray-700">
                Set to 23°C, a 1-ton split unit eats 4 kWh in a typical Dubai
                summer evening: 2.0 kg CO₂. Nudge to 25°C + ceiling fan and you
                cut the load by 25%—saving 0.5 kg every night and slashing your
                DEWA bill ~AED 150/year.
              </p>
              <p className="mt-3 text-sm italic">
                Human lens: "That's like leaving a 60-W bulb burning non-stop
                for 11 days."
              </p>
            </div>

            <div className="mt-8 flex justify-between">
              <button
                onClick={() => setActiveSection(2)}
                className="px-4 py-2 rounded-md border border-primary-500 text-primary-500 hover:bg-primary-50"
              >
                ← Back to Your Emissions
              </button>
              <button
                onClick={() => {
                  handleSectionComplete(3);
                  setActiveSection(4);
                }}
                className="px-4 py-2 rounded-md bg-[#349383] text-white hover:bg-primary-700"
              >
                Interactive Zone →
              </button>
            </div>
          </div>
        )}

        {activeSection === 4 && (
          <div className="bg-white shadow-md rounded-lg p-6">
            <h2 className="text-2xl font-semibold text-gray-800 mb-6">
              Interactive Zone
            </h2>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 mb-12">
              {/* Rank-the-Impact Game */}
              <div className="bg-primary-50 p-6 rounded-lg">
                <h3 className="text-lg font-medium mb-4">Rank the Impact</h3>
                <p className="mb-4 text-sm text-gray-700">
                  Drag these common activities into order from highest to lowest
                  carbon impact.
                </p>

                <div className="space-y-2 mb-6">
                  {rankingItems.map((item) => (
                    <div
                      key={item.id}
                      draggable
                      onDragStart={(e) => handleDragStart(e, item.id)}
                      onDragOver={(e) => handleDragOver(e, item.id)}
                      onDragEnd={handleDragEnd}
                      className={`bg-white p-3 rounded-md shadow-sm cursor-move border ${
                        draggedItemId === item.id
                          ? "border-primary-500"
                          : showRankingResults
                          ? item.isCorrect
                            ? "border-green-500"
                            : "border-red-500"
                          : "border-gray-200"
                      }`}
                    >
                      <div className="flex justify-between items-center">
                        {showRankingResults && (
                          <span className="font-medium w-6 h-6 flex items-center justify-center bg-gray-100 rounded-full mr-2">
                            {item.correctRank}
                          </span>
                        )}

                        <span className="flex-1">{item.name}</span>

                        {showRankingResults && (
                          <span className="text-gray-500 text-sm">
                            {item.value} kg CO₂e
                          </span>
                        )}
                      </div>
                    </div>
                  ))}
                </div>

                {!showRankingResults ? (
                  <button
                    onClick={checkRanking}
                    className="w-full py-2 bg-[#349383] text-white rounded-md hover:bg-primary-700"
                  >
                    Check My Ranking
                  </button>
                ) : (
                  <button
                    onClick={resetRanking}
                    className="w-full py-2 bg-[#349383] text-white rounded-md hover:bg-primary-700"
                  >
                    Try Again
                  </button>
                )}
              </div>

              {/* Day Simulator */}
              <div className="bg-primary-50 p-6 rounded-lg">
                <h3 className="text-lg font-medium mb-4">
                  Your 14-kg Day Simulator
                </h3>
                <p className="mb-4 text-sm text-gray-700">
                  Make three decisions for a day, keeping your total under the
                  UAE average (14 kg).
                </p>

                <div className="space-y-6">
                  <div>
                    <h4 className="font-medium mb-2">Breakfast Choice:</h4>
                    <div className="grid grid-cols-3 gap-2">
                      <button
                        className={`p-2 bg-white rounded border hover:bg-gray-50 text-sm ${
                          simulatorChoices.breakfast === "beef"
                            ? "border-2 border-primary-500 bg-primary-50"
                            : ""
                        }`}
                        onClick={() =>
                          handleSimulatorChoice("breakfast", "beef")
                        }
                      >
                        Beef Breakfast
                        <span className="block text-xs text-red-500 mt-1">
                          +3.5 kg CO₂e
                        </span>
                      </button>
                      <button
                        className={`p-2 bg-white rounded border hover:bg-gray-50 text-sm ${
                          simulatorChoices.breakfast === "eggs"
                            ? "border-2 border-primary-500 bg-primary-50"
                            : ""
                        }`}
                        onClick={() =>
                          handleSimulatorChoice("breakfast", "eggs")
                        }
                      >
                        Eggs & Toast
                        <span className="block text-xs text-amber-500 mt-1">
                          +1.5 kg CO₂e
                        </span>
                      </button>
                      <button
                        className={`p-2 bg-white rounded border hover:bg-gray-50 text-sm ${
                          simulatorChoices.breakfast === "fruit"
                            ? "border-2 border-primary-500 bg-primary-50"
                            : ""
                        }`}
                        onClick={() =>
                          handleSimulatorChoice("breakfast", "fruit")
                        }
                      >
                        Fruit & Cereal
                        <span className="block text-xs text-green-500 mt-1">
                          +0.3 kg CO₂e
                        </span>
                      </button>
                    </div>
                  </div>

                  <div>
                    <h4 className="font-medium mb-2">Commute Choice:</h4>
                    <div className="grid grid-cols-3 gap-2">
                      <button
                        className={`p-2 bg-white rounded border hover:bg-gray-50 text-sm ${
                          simulatorChoices.commute === "solo"
                            ? "border-2 border-primary-500 bg-primary-50"
                            : ""
                        }`}
                        onClick={() => handleSimulatorChoice("commute", "solo")}
                      >
                        Drive Solo
                        <span className="block text-xs text-red-500 mt-1">
                          +3.8 kg CO₂e
                        </span>
                      </button>
                      <button
                        className={`p-2 bg-white rounded border hover:bg-gray-50 text-sm ${
                          simulatorChoices.commute === "carpool"
                            ? "border-2 border-primary-500 bg-primary-50"
                            : ""
                        }`}
                        onClick={() =>
                          handleSimulatorChoice("commute", "carpool")
                        }
                      >
                        Carpool
                        <span className="block text-xs text-amber-500 mt-1">
                          +1.3 kg CO₂e
                        </span>
                      </button>
                      <button
                        className={`p-2 bg-white rounded border hover:bg-gray-50 text-sm ${
                          simulatorChoices.commute === "metro"
                            ? "border-2 border-primary-500 bg-primary-50"
                            : ""
                        }`}
                        onClick={() =>
                          handleSimulatorChoice("commute", "metro")
                        }
                      >
                        Metro
                        <span className="block text-xs text-green-500 mt-1">
                          +0.7 kg CO₂e
                        </span>
                      </button>
                    </div>
                  </div>

                  <div>
                    <h4 className="font-medium mb-2">Evening Cooling:</h4>
                    <div className="grid grid-cols-3 gap-2">
                      <button
                        className={`p-2 bg-white rounded border hover:bg-gray-50 text-sm ${
                          simulatorChoices.cooling === "19c"
                            ? "border-2 border-primary-500 bg-primary-50"
                            : ""
                        }`}
                        onClick={() => handleSimulatorChoice("cooling", "19c")}
                      >
                        AC at 19°C
                        <span className="block text-xs text-red-500 mt-1">
                          +3.0 kg CO₂e
                        </span>
                      </button>
                      <button
                        className={`p-2 bg-white rounded border hover:bg-gray-50 text-sm ${
                          simulatorChoices.cooling === "22c"
                            ? "border-2 border-primary-500 bg-primary-50"
                            : ""
                        }`}
                        onClick={() => handleSimulatorChoice("cooling", "22c")}
                      >
                        AC at 22°C
                        <span className="block text-xs text-amber-500 mt-1">
                          +2.2 kg CO₂e
                        </span>
                      </button>
                      <button
                        className={`p-2 bg-white rounded border hover:bg-gray-50 text-sm ${
                          simulatorChoices.cooling === "25c"
                            ? "border-2 border-primary-500 bg-primary-50"
                            : ""
                        }`}
                        onClick={() => handleSimulatorChoice("cooling", "25c")}
                      >
                        AC at 25°C + Fan
                        <span className="block text-xs text-green-500 mt-1">
                          +1.2 kg CO₂e
                        </span>
                      </button>
                    </div>
                  </div>
                </div>

                <div className="mt-6 flex items-center justify-between">
                  <span className="font-medium">Your Daily Total:</span>
                  <div className="flex items-center space-x-2">
                    <div className="w-24 h-6 bg-gray-200 rounded-full overflow-hidden">
                      <div
                        className={`h-full ${
                          simulatorTotal > 14 ? "bg-red-500" : "bg-primary-600"
                        } rounded-full`}
                        style={{
                          width: `${Math.min(
                            (simulatorTotal / 20) * 100,
                            100
                          )}%`,
                        }}
                      ></div>
                    </div>
                    <span
                      className={`font-bold ${
                        simulatorTotal > 14 ? "text-red-500" : ""
                      }`}
                    >
                      {simulatorTotal.toFixed(1)} kg
                    </span>
                  </div>
                </div>

                <div className="mt-2 text-sm text-gray-600">
                  <div className="flex justify-between">
                    <span>UAE Average: 14 kg</span>
                    <span>Global Average: 5 kg</span>
                  </div>
                </div>

                <button
                  className="w-full py-2 mt-4 bg-[#349383] text-white rounded-md hover:bg-primary-700"
                  onClick={resetSimulator}
                >
                  Reset Simulator
                </button>
              </div>
            </div>

            {/* Life-Lens Pledge */}
            <div className="bg-primary-50 p-6 rounded-lg">
              <h3 className="text-lg font-medium mb-4">Life-Lens Pledge</h3>
              <p className="mb-4 text-sm text-gray-700">
                Choose one habit to change and see the annual impact. Then write
                your one-sentence plan.
              </p>

              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
                <button
                  className={`p-4 bg-white rounded-md shadow-sm border hover:border-primary-500 text-center ${
                    selectedPledge === "beef-plant"
                      ? "border-2 border-primary-500 bg-primary-50"
                      : ""
                  }`}
                  onClick={() => handlePledgeSelect("beef-plant", 500)}
                >
                  <span className="block text-2xl mb-2">🥩 → 🌱</span>
                  <span className="font-medium">Beef to Plant-Based</span>
                  <span className="block text-sm text-green-600 mt-1">
                    −500 kg CO₂/year
                  </span>
                </button>

                <button
                  className={`p-4 bg-white rounded-md shadow-sm border hover:border-primary-500 text-center ${
                    selectedPledge === "drive-transport"
                      ? "border-2 border-primary-500 bg-primary-50"
                      : ""
                  }`}
                  onClick={() => handlePledgeSelect("drive-transport", 450)}
                >
                  <span className="block text-2xl mb-2">🚗 → 🚌</span>
                  <span className="font-medium">
                    Solo Drive to Public Transport
                  </span>
                  <span className="block text-sm text-green-600 mt-1">
                    −450 kg CO₂/year
                  </span>
                </button>

                <button
                  className={`p-4 bg-white rounded-md shadow-sm border hover:border-primary-500 text-center ${
                    selectedPledge === "ac-fan"
                      ? "border-2 border-primary-500 bg-primary-50"
                      : ""
                  }`}
                  onClick={() => handlePledgeSelect("ac-fan", 380)}
                >
                  <span className="block text-2xl mb-2">❄️ → 🌡️</span>
                  <span className="font-medium">AC at 20°C to 24°C + Fan</span>
                  <span className="block text-sm text-green-600 mt-1">
                    −380 kg CO₂/year
                  </span>
                </button>

                <button
                  className={`p-4 bg-white rounded-md shadow-sm border hover:border-primary-500 text-center ${
                    selectedPledge === "bottled-filtered"
                      ? "border-2 border-primary-500 bg-primary-50"
                      : ""
                  }`}
                  onClick={() => handlePledgeSelect("bottled-filtered", 41)}
                >
                  <span className="block text-2xl mb-2">🧴 → 🍶</span>
                  <span className="font-medium">Bottled to Filtered Water</span>
                  <span className="block text-sm text-green-600 mt-1">
                    −41 kg CO₂/year
                  </span>
                </button>
              </div>

              <div className="mb-6">
                <label className="block text-sm font-medium mb-2">
                  My pledge (one sentence):
                </label>
                <textarea
                  className="w-full p-3 border rounded-md"
                  rows="2"
                  placeholder="I commit to..."
                  value={pledgeMade}
                  onChange={(e) => setPledgeMade(e.target.value)}
                ></textarea>
              </div>

              <button
                className="w-full py-2 bg-[#349383] text-white rounded-md hover:bg-primary-700 disabled:bg-gray-400"
                disabled={!pledgeMade || !selectedPledge}
                onClick={savePledge}
              >
                Save My Pledge
              </button>
            </div>

            {/* Quick-start cheat-sheet */}
            <div className="mt-8 bg-white border p-5 rounded-lg">
              <h3 className="text-lg font-medium mb-4">
                Quick-start cheat-sheet
              </h3>
              <div className="bg-gray-50 p-4 rounded-md text-sm font-mono">
                <pre className="text-gray-800">
                  {`TOP UAE SWAPS
🥩 Beef → 🌱 Lentils ............ −4.8 kg
🚗 Solo 10 km → 🚌 Bus ........ −1.5 kg
❄️ 23 °C → 25 °C + fan ........ −0.5 kg
🧴 Bottled → Tap (1 L) ........ −0.082 kg`}
                </pre>
              </div>

              <div className="mt-4 flex justify-center">
                <button className="py-2 px-4 bg-primary-600 text-white rounded-md hover:bg-primary-700 flex items-center">
                  <svg
                    className="h-4 w-4 mr-2"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4"
                    />
                  </svg>
                  Download Cheat-sheet
                </button>
              </div>
            </div>

            <div className="mt-8 flex justify-between">
              <button
                onClick={() => setActiveSection(3)}
                className="px-4 py-2 rounded-md border border-primary-500 text-primary-500 hover:bg-primary-50"
              >
                ← Back to Emissions Deep-Dive
              </button>
              <button
                onClick={() => {
                  handleSectionComplete(4);
                  setActiveSection(5);
                }}
                className="px-4 py-2 rounded-md bg-[#349383] text-white hover:bg-primary-700"
              >
                Make Your Pledge →
              </button>
            </div>
          </div>
        )}

        {activeSection === 5 && (
          <div className="bg-white shadow-md rounded-lg p-6">
            <h2 className="text-2xl font-semibold text-gray-800 mb-6">
              Your Pledge
            </h2>

            <div className="text-center mb-8">
              <div className="mb-6">
                <span className="inline-block p-4 bg-primary-100 rounded-full">
                  <Check className="h-12 w-12 text-primary-600" />
                </span>
              </div>

              <h3 className="text-xl font-medium mb-3">
                Pick one habit you'll test this week.
              </h3>
              <p className="text-gray-700">
                Tell a friend, tick it off in the tracker, and watch your carbon
                bar shrink. Tiny shifts, repeated daily, turn into tonnes off
                the ledger.
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-10">
              <div className="bg-primary-50 p-5 rounded-lg">
                <h4 className="font-medium mb-4">My Carbon Pledge:</h4>

                {pledgeDone ? (
                  <div className="p-4 bg-white rounded-md">
                    <p className="italic">{pledgeMade}</p>
                    {selectedPledge && (
                      <div className="mt-2 text-sm">
                        <div className="font-medium text-primary-600">
                          Selected pledge:
                        </div>
                        <div className="mt-1">
                          {selectedPledge === "beef-plant" &&
                            "Switch from beef to plant-based meals"}
                          {selectedPledge === "drive-transport" &&
                            "Use public transport instead of driving alone"}
                          {selectedPledge === "ac-fan" &&
                            "Increase AC temperature and use a fan"}
                          {selectedPledge === "bottled-filtered" &&
                            "Switch from bottled to filtered water"}
                        </div>
                      </div>
                    )}
                  </div>
                ) : (
                  <div className="p-4 border border-dashed rounded-md bg-white">
                    <p className="text-gray-500 italic">
                      Return to the Interactive Zone to make your pledge...
                    </p>
                    <button
                      onClick={() => setActiveSection(5)}
                      className="mt-2 text-primary-600 text-sm font-medium hover:underline"
                    >
                      Go to Interactive Zone →
                    </button>
                  </div>
                )}

                <div className="mt-4">
                  <h5 className="font-medium mb-2">Carbon savings:</h5>
                  <div className="bg-white p-3 rounded-md">
                    <div className="flex items-center justify-between">
                      <span>Per week:</span>
                      <span className="font-medium text-primary-600">
                        ~{pledgeSavings.weekly} kg CO₂e
                      </span>
                    </div>
                    <div className="flex items-center justify-between mt-1">
                      <span>Per year:</span>
                      <span className="font-medium text-primary-600">
                        ~{pledgeSavings.yearly} kg CO₂e
                      </span>
                    </div>
                    <div className="mt-2 text-sm text-gray-600">
                      Equivalent to planting approximately{" "}
                      {Math.round(pledgeSavings.yearly / 20)} trees!
                    </div>
                  </div>
                </div>

                {showEmailForm && (
                  <div className="mt-4 bg-blue-50 p-4 rounded-md">
                    <h5 className="font-medium mb-2">
                      Stay on track with reminders:
                    </h5>
                    <div className="mb-3">
                      <label className="block text-sm mb-1">
                        Your email (optional):
                      </label>
                      <input
                        type="email"
                        className="w-full p-2 border rounded"
                        placeholder="email@example.com"
                        value={userEmail}
                        onChange={(e) => setUserEmail(e.target.value)}
                      />
                    </div>
                    <button
                      className="w-full py-2 bg-[#349383] text-white rounded-md hover:bg-primary-700"
                      onClick={saveEmail}
                    >
                      Set Up Reminders
                    </button>
                    <p className="text-xs text-gray-500 mt-2">
                      We'll send gentle check-ins to help you keep your pledge.
                    </p>
                  </div>
                )}
              </div>

              <div className="bg-primary-50 p-5 rounded-lg">
                <h4 className="font-medium mb-4">Track Your Progress:</h4>

                <div className="bg-white p-4 rounded-md">
                  <h5 className="font-medium mb-3">Weekly Habit Tracker</h5>

                  <div className="space-y-3">
                    {trackerWeeks.map((week) => (
                      <div key={week.id} className="flex">
                        <div className="w-24 font-medium text-gray-600">
                          {week.name}:
                        </div>
                        <div className="flex space-x-2">
                          {week.days.map((isChecked, dayIndex) => (
                            <button
                              key={dayIndex}
                              className={`h-6 w-6 rounded-full border flex items-center justify-center transition-colors
                                ${
                                  isChecked
                                    ? "border-primary-500 bg-primary-500 text-white"
                                    : "border-gray-300 bg-white hover:bg-gray-100"
                                }`}
                              onClick={() =>
                                toggleTrackerDay(week.id, dayIndex)
                              }
                            >
                              {isChecked
                                ? "✓"
                                : ["M", "T", "W", "T", "F", "S", "S"][dayIndex]}
                            </button>
                          ))}
                        </div>
                      </div>
                    ))}
                  </div>

                  <p className="text-sm text-gray-500 mt-3">
                    Click on a day to mark your success!
                  </p>
                </div>
              </div>
            </div>

            <div className="bg-primary-100 p-6 rounded-lg text-center">
              <h3 className="font-medium mb-2">
                Ready to take your knowledge to the next level?
              </h3>
              <p className="mb-4">
                Explore real case studies of carbon reduction in the next
                module.
              </p>

              {resultsSaved ? (
                <div className="bg-green-100 text-green-800 px-4 py-3 rounded-md flex items-center justify-center mb-4">
                  <Check className="h-5 w-5 mr-2 text-green-600" />
                  <span>
                    Module completed! You can now proceed to Case Studies.
                  </span>
                </div>
              ) : (
                <button
                  onClick={handleModuleComplete}
                  className="px-6 py-2 bg-[#349383] text-white rounded-md hover:bg-primary-700"
                >
                  Complete This Module
                </button>
              )}
            </div>

            <div className="mt-8 flex justify-between">
              <button
                onClick={() => setActiveSection(4)}
                className="px-4 py-2 rounded-md border border-primary-500 text-primary-500 hover:bg-primary-50"
              >
                ← Back to Interactive Zone
              </button>
              <button
                onClick={handleModuleComplete}
                className="px-4 py-2 rounded-md bg-[#349383] text-white hover:bg-primary-700"
              >
                Complete Module
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Module Navigation */}
      <ModuleNavigation
        prevLink="/carbon-emissions-101"
        prevText="Previous: Carbon Emissions 101"
        nextLink="/case-studies"
        nextText="Next: Case Studies"
        onMarkComplete={() => handleModuleComplete()}
        isCompleted={resultsSaved}
        completeButtonId="module-complete-button"
      />
    </div>
  );
};

export default UAECarbonDay;
